import { Tooltip } from "@/components/atoms/Tooltip";
import { cn } from "@/lib/cn";
import { formatMinutes, type SlotProjection } from "./slotProjection";
import { colorKeyFor, hueForKey, type ColorMode } from "./sceneTheme";

interface Props {
  projection: SlotProjection;
  colorMode: ColorMode;
  soloKey: string | null;
}

/**
 * One lane per weekday with a block where each slot falls — the precise
 * counterpart to the 3D plate. Exact start times are readable without hovering,
 * which is what the plate trades away for shape.
 */
export function SlotLanes({ projection, colorMode, soloKey }: Props) {
  const windowStart = projection.windowStartMinutes ?? 0;
  const windowEnd = projection.windowEndMinutes ?? 0;
  const span = Math.max(1, windowEnd - windowStart);
  const pct = (minutes: number) => ((minutes - windowStart) / span) * 100;

  let ordinal = 0;

  return (
    <div className="absolute inset-0 flex flex-col gap-2 px-3 py-3">
      <div className="flex justify-between font-mono text-[11.5px] text-bridge-subtle">
        <span>{formatMinutes(windowStart)}</span>
        <span>{formatMinutes(windowEnd)}</span>
      </div>

      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
        {projection.days.map((day) => (
          <div key={day.weekdayCode} className="flex items-center gap-2">
            <span
              className={cn(
                "w-9 shrink-0 font-mono text-[11.5px] uppercase tracking-wider",
                day.active ? "text-bridge-muted" : "text-bridge-subtle",
              )}
            >
              {day.weekdayLabel}
            </span>
            <div className="relative h-6 flex-1 rounded-[4px] bg-bridge-bg">
              {day.slots.map((slot, rowIndex) => {
                const key = colorKeyFor(slot, colorMode, ordinal++, rowIndex, day.slots.length);
                const dimmed = soloKey != null && key !== soloKey;
                return (
                  <Tooltip
                    key={slot.startMinutes}
                    side="top"
                    content={`${day.weekdayLabel} ${formatMinutes(slot.startMinutes)}–${formatMinutes(slot.endMinutes)} · ${slot.user?.name ?? "Unassigned"} · ${slot.capacity} lead${slot.capacity === 1 ? "" : "s"}${slot.blocked ? " · not bookable" : ""}`}
                  >
                    <span
                      className="absolute top-0 block h-6 rounded-[3px] transition-opacity"
                      style={{
                        left: `${pct(slot.startMinutes)}%`,
                        // The 2px inset is the same guaranteed gap the plate
                        // uses, so a dense interval never reads as one block.
                        width: `calc(${Math.max(0.5, pct(slot.endMinutes) - pct(slot.startMinutes))}% - 2px)`,
                        background: hueForKey(key, projection.active),
                        boxShadow: "inset 0 0 0 1px rgba(13,21,36,.22)",
                        opacity: dimmed ? 0.18 : 1,
                      }}
                    />
                  </Tooltip>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {projection.days.every((d) => d.slots.length === 0) && (
        <p className="text-center text-xs text-bridge-subtle">No slots to show yet.</p>
      )}
    </div>
  );
}
